import {
    useState, useCallback, useEffect, useRef
} from 'react';
import {
    getPresignedUrls, listCollectionFiles, processCollection, deleteCollectionFiles
} from '../configApi';
import {
    createWebSocketClient
} from 'shared/api/clients/knowledgeBuilder.httpClient';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    Card, CardContent, CardHeader
} from 'shared/components/ui/card';
import {
    Upload, CheckCircle2, XCircle, Loader2, FileText, RefreshCw, Cog, AlertCircle, Database, Trash2
} from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from 'shared/components/ui/alert-dialog';

function formatBytes(bytes) {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}

function ProgressItem({
    name, status, progress
}) {
    return (
        <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
                <p className="text-xs truncate">{name}</p>
                {status === 'uploading' && (
                    <div className="mt-1 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                        <div
                            className="h-full bg-primary rounded-full transition-all duration-200"
                            style={{
                                width: `${progress}%`
                            }}
                        />
                    </div>
                )}
            </div>
            <div className="shrink-0">
                {status === 'pending' && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                {status === 'uploading' && <span className="text-xs text-muted-foreground">{progress}%</span>}
                {status === 'done' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                {status === 'error' && <XCircle className="h-4 w-4 text-destructive" />}
            </div>
        </div>
    );
}

function CollectionCard({
    col, instanceId, configKey, organizationId
}) {
    const [files, setFiles] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(true);
    const [showUpload, setShowUpload] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [prefix, setPrefix] = useState('');
    const [uploading, setUploading] = useState(false);
    const [fileProgress, setFileProgress] = useState([]);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [l1Status, setL1Status] = useState(null); // 'processing' | 'completed' | 'error' | 'cancelled' | null
    const [l1Message, setL1Message] = useState(null);
    const wsRef = useRef(null);

    // Connect to KB WebSocket for L1 status
    useEffect(() => {
        if (!organizationId || !col._id) return;

        const ws = createWebSocketClient(
            `/ws/processing_status/${col._id}?organization_id=${organizationId}&processing_type=l1`
        );

        ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                setL1Status(message.status);
                setL1Message(message.message || null);

                if (message.status === 'completed' || message.status === 'success') {
                    setProcessing(false);
                    loadFiles();
                } else if (message.status === 'error' || message.status === 'failed' || message.status === 'cancelled') {
                    setProcessing(false);
                } else if (message.status === 'processing') {
                    setProcessing(true);
                }
            } catch { /* ignore parse errors */ }
        };

        wsRef.current = ws;

        return () => {
            if (ws.readyState === WebSocket.OPEN) {
                ws.close();
            }
        };
    }, [organizationId, col._id]);

    const loadFiles = useCallback(async () => {
        setLoadingFiles(true);
        try {
            const data = await listCollectionFiles(instanceId, configKey, col._id);
            setFiles(data.documents || []);
        } catch (e) {
            setFiles([]);
        } finally {
            setLoadingFiles(false);
        }
    }, [instanceId, configKey, col._id]);

    useEffect(() => { loadFiles(); }, [loadFiles]);

    const openUpload = () => {
        setShowUpload(true);
        setSelectedFiles([]);
        setPrefix('');
        setFileProgress([]);
        setError(null);
    };

    const setFileStatus = (name, patch) =>
        setFileProgress(prev => prev.map(f => f.name === name ? {
            ...f,
            ...patch
        } : f));

    const triggerL1Processing = async () => {
        setProcessing(true);
        setL1Status('processing');
        setL1Message('Starting L1 processing...');
        try {
            await processCollection(instanceId, configKey, col._id);
            // Status updates will now come through the WebSocket
        } catch (e) {
            setProcessing(false);
            setL1Status('error');
            setL1Message(e.message || 'Failed to trigger processing');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteCollectionFiles(instanceId, configKey, col._id, [deleteTarget]);
            await loadFiles();
        } catch (e) {
            setError(e.message || 'Failed to delete file');
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };

    const handleUpload = async () => {
        if (!selectedFiles.length) { setError('Select at least one file.'); return; }
        setUploading(true);
        setError(null);
        setFileProgress(selectedFiles.map(f => ({
            name: f.name,
            status: 'pending',
            progress: 0
        })));

        try {
            const fileNames = selectedFiles.map(f => f.name);
            const {
                preSignedUrls
            } = await getPresignedUrls(instanceId, configKey, col._id, fileNames, prefix);

            let hasErrors = false;
            await Promise.all(
                preSignedUrls.map(async ({
                    fileName, url
                }) => {
                    const file = selectedFiles.find(f => f.name === fileName);
                    setFileStatus(fileName, {
                        status: 'uploading',
                        progress: 0
                    });
                    try {
                        await new Promise((resolve, reject) => {
                            const xhr = new XMLHttpRequest();
                            xhr.open('PUT', url);
                            xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
                            xhr.upload.onprogress = (e) => {
                                if (e.lengthComputable) {
                                    setFileStatus(fileName, {
                                        progress: Math.round((e.loaded / e.total) * 100)
                                    });
                                }
                            };
                            xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`));
                            xhr.onerror = () => reject(new Error('Network error'));
                            xhr.send(file);
                        });
                        setFileStatus(fileName, {
                            status: 'done',
                            progress: 100
                        });
                    } catch {
                        setFileStatus(fileName, {
                            status: 'error'
                        });
                        hasErrors = true;
                    }
                })
            );

            await loadFiles();

            // Auto-trigger L1 processing after successful upload
            if (!hasErrors) {
                await triggerL1Processing();
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setUploading(false);
        }
    };

    const allDone = fileProgress.length > 0 && fileProgress.every(f => f.status === 'done');
    const isActive = l1Status === 'processing';
    const isSuccess = l1Status === 'completed' || l1Status === 'success';
    const isError = l1Status === 'error' || l1Status === 'failed' || l1Status === 'cancelled';

    return (
        <Card>
            <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="font-medium text-sm truncate">{col.name || col._id}</span>
                        {col.vectorStoreType && <Badge variant="secondary" className="text-xs shrink-0">{col.vectorStoreType}</Badge>}
                        {files.length > 0 && <span className="text-xs text-muted-foreground shrink-0">{files.length} file{files.length !== 1 ? 's' : ''}</span>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <Button variant="outline" size="sm" className="gap-1.5" onClick={triggerL1Processing} disabled={processing}>
                            <Cog className={`h-3.5 w-3.5 ${processing ? 'animate-spin' : ''}`} />
                            {processing ? 'Processing...' : 'Process'}
                        </Button>
                        <Button variant="outline" size="sm" className="gap-1.5" onClick={openUpload}>
                            <Upload className="h-3.5 w-3.5" /> Upload
                        </Button>
                    </div>
                </div>
            </CardHeader>

            <CardContent className="border-t pt-0 pb-0">
                {/* L1 Processing Status */}
                {l1Status && l1Status !== 'idle' && (
                    <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 my-3 ${
                        isActive ? 'bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800' :
                            isSuccess ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' :
                                isError ? 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800' :
                                    'bg-muted border-border'
                    }`}>
                        {isActive && <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400 shrink-0" />}
                        {isSuccess && <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />}
                        {isError && <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0" />}
                        <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${
                                isActive ? 'text-blue-700 dark:text-blue-300' :
                                    isSuccess ? 'text-green-700 dark:text-green-300' :
                                        isError ? 'text-red-700 dark:text-red-300' :
                                            'text-foreground'
                            }`}>
                                {isActive ? 'Processing documents...' : isSuccess ? 'Processing complete' : isError ? 'Processing failed' : l1Status}
                            </p>
                            {l1Message && <p className="text-xs text-muted-foreground mt-0.5 truncate">{l1Message}</p>}
                        </div>
                        {(isSuccess || isError) && (
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs shrink-0"
                                onClick={() => { setL1Status(null); setL1Message(null); }}>
                                Dismiss
                            </Button>
                        )}
                    </div>
                )}

                {showUpload && (
                    <div className="space-y-4 py-4 border-b">
                        <div className="space-y-1.5">
                            <Label>Folder / Prefix <span className="text-muted-foreground font-normal text-xs">(optional)</span></Label>
                            <Input placeholder="e.g. docs/" value={prefix} onChange={e => setPrefix(e.target.value)} disabled={uploading} />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Files</Label>
                            <input
                                type="file"
                                multiple
                                disabled={uploading}
                                className="block w-full text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:text-xs file:font-medium file:cursor-pointer"
                                onChange={e => { setSelectedFiles(Array.from(e.target.files)); setFileProgress([]); }}
                            />
                        </div>
                        {selectedFiles.length > 0 && fileProgress.length === 0 && (
                            <p className="text-xs text-muted-foreground">{selectedFiles.length} file(s) selected</p>
                        )}
                        {fileProgress.length > 0 && (
                            <div className="space-y-2 border rounded-md p-3 bg-muted/30">
                                {fileProgress.map(f => <ProgressItem key={f.name} {...f} />)}
                            </div>
                        )}
                        {error && <p className="text-sm text-destructive">{error}</p>}
                        {allDone && (
                            <p className="text-sm text-green-600 flex items-center gap-1">
                                <CheckCircle2 className="h-4 w-4" /> All files uploaded{processing ? ' â€” processing started...' : '.'}
                            </p>
                        )}
                        <div className="flex gap-2">
                            {!allDone && (
                                <Button onClick={handleUpload} disabled={uploading || !selectedFiles.length} size="sm">
                                    {uploading ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Uploading...</> : 'Upload'}
                                </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => { setShowUpload(false); setFileProgress([]); setError(null); }} disabled={uploading || processing}>
                                {allDone ? 'Close' : 'Cancel'}
                            </Button>
                        </div>
                    </div>
                )}

                <div className="py-3">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-muted-foreground">Files</span>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={loadFiles} disabled={loadingFiles}>
                            <RefreshCw className={`h-3 w-3 ${loadingFiles ? 'animate-spin' : ''}`} />
                        </Button>
                    </div>
                    {loadingFiles ? (
                        <div className="flex items-center gap-2 py-4 justify-center">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Loading files...</span>
                        </div>
                    ) : files.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-4 text-center">No files uploaded yet.</p>
                    ) : (
                        <div className="space-y-1">
                            {files.map((file) => (
                                <div key={file.Key} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50 group">
                                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                                    <span className="text-xs flex-1 truncate" title={file.Key}>{file.Key}</span>
                                    <span className="text-xs text-muted-foreground shrink-0">{formatBytes(file.Size)}</span>
                                    <span className="text-xs text-muted-foreground shrink-0 hidden group-hover:inline">{formatDate(file.LastModified)}</span>
                                    {file.presigned_url && (
                                        <a
                                            href={file.presigned_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs text-primary shrink-0 hidden group-hover:inline hover:underline"
                                        >
                                            Download
                                        </a>
                                    )}
                                    <button
                                        onClick={() => setDeleteTarget(file.Key)}
                                        className="shrink-0 hidden group-hover:inline-flex items-center justify-center h-6 w-6 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                                        title="Delete file"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>

            <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete file</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete <span className="font-medium text-foreground">{deleteTarget}</span>? This will also remove its embeddings. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            {deleting ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Deleting...</> : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}

export default function CollectionFiles({
    collections, instanceId, configKey, organizationId
}) {
    if (!collections || collections.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="h-14 w-14 rounded-full bg-[#b1092a]/10 flex items-center justify-center mb-4">
                    <Database className="h-7 w-7 text-[#b1092a]" />
                </div>
                <h3 className="text-base font-semibold mb-1">No collections available</h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                    Collections store documents and files that your assistant uses as a knowledge base for answering questions.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-3 max-w-2xl">
            {collections.map(col => (
                <CollectionCard key={col._id} col={col} instanceId={instanceId} configKey={configKey} organizationId={organizationId} />
            ))}
        </div>
    );
}
